import { useState, useEffect, useRef } from "react";
import { toast } from "../hooks/useToast";
import { ChatPedido } from "./ChatPedido";
import { CheckoutLoja } from "./CheckoutLoja";
import { fmtMoeda } from "../utils/format";
import { Lightbox } from "./ui/Lightbox";
import { categoriasDoProduto, emojiDaCategoria } from "../utils/categoriasProduto";
import { StoreProductCard } from "./StoreProductCard";
import { CalcIcon } from "./calculadora/CalcIcon";
import { lerCarrinho, salvarCarrinho, reconciliarCarrinho, aguardarLoginCheckout, checkoutAguardandoLogin } from "../utils/carrinhoLoja";
import { lerCupomLoja, salvarCupomLoja, normalizarCupom, validarCarrinhoLoja, mensagemPedidoLoja } from "../utils/pedidosLoja";
import "./storefront.css";
import "./storeCart.css";
import { useStoreDialog } from "../hooks/useStoreDialog";
import { Visualizador3D } from "./Visualizador3D";
import { visualizacao3dDoItem } from "../utils/visualizacao3d";

// Fotos/vídeos pra exibir de um produto: prioriza a galeria nova (Storage);
// produto antigo sem galeria cai pra foto única (fotoBase64); sem nenhuma,
// lista vazia. Mantém {url,tipo} (não só a url) pra saber renderizar vídeo.
const fotosDoProduto = (p) => p?.fotos?.length ? p.fotos.map(f => ({ url:f.url, tipo:f.tipo })) : p?.fotoBase64 ? [{ url:p.fotoBase64 }] : [];

// Slide de vídeo do carrossel: começa a tocar sozinho assim que aparece, sem
// clique. O navegador só libera autoplay se o vídeo estiver MUDO (e, no iOS,
// inline) — o som fica pro zoom (Lightbox). `muted` é setado pela propriedade
// porque o React não o reflete como atributo e o Safari recusa o autoplay sem
// isso. Fica em loop (senão congela no último quadro) e pausa enquanto o zoom
// está aberto por cima, pra não tocar duas vezes.
function VideoSlide({ url, pausado, onClick }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    if (pausado) el.pause();
    else Promise.resolve(el.play()).catch(() => {}); // autoplay bloqueado (ex.: economia de dados): fica no 1º quadro, o clique abre o zoom
  }, [url, pausado]);
  return <video ref={ref} src={url} autoPlay muted loop playsInline preload="auto" onClick={onClick}
    style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />;
}

export function SitePublicoLoja({ produtos, vendedor, cliente, onPedirLogin, categorias = [], authLoading = false }) {
  const [filtro, setFiltro] = useState("todos");
  const [filtroCat, setFiltroCat] = useState("todas");
  const [busca, setBusca] = useState("");
  const [carrinho, setCarrinho] = useState(() => lerCarrinho(vendedor?.id, produtos));
  const [codigoCupom, setCodigoCupom] = useState(() => lerCupomLoja(vendedor?.id));
  const [cupomInput, setCupomInput] = useState("");
  const [cotacao, setCotacao] = useState(null);
  const [cupomErro, setCupomErro] = useState("");
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [tentativaCupom, setTentativaCupom] = useState(0);
  const [carrinhoAberto, setCarrinhoAberto] = useState(false);
  const [checkoutAberto, setCheckoutAberto] = useState(false);
  const [pedidoCriado, setPedidoCriado] = useState(null); // { id } -> abre chat
  const [produtoDetalhe, setProdutoDetalhe] = useState(null);
  const [fotoIdxDetalhe, setFotoIdxDetalhe] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const cartRef = useRef(null), detailRef = useRef(null);
  useStoreDialog(cartRef,carrinhoAberto,() => setCarrinhoAberto(false));
  useStoreDialog(detailRef,!!produtoDetalhe,() => setProdutoDetalhe(null));
  useEffect(() => { setFotoIdxDetalhe(0); setLightboxIdx(null); }, [produtoDetalhe?.id]);

  const vendedorId = vendedor?.id;
  const chaveCarrinho = JSON.stringify(carrinho.map(({ id, qtd, preco }) => ({ id, qtd, preco })));
  useEffect(() => { salvarCupomLoja(vendedorId, codigoCupom); }, [vendedorId, codigoCupom]);
  useEffect(() => {
    let atual = true;
    setCotacao(null);
    setCupomErro("");
    if (!codigoCupom || !vendedorId || chaveCarrinho === "[]") { setValidandoCupom(false); return; }
    setValidandoCupom(true);
    validarCarrinhoLoja(vendedorId, JSON.parse(chaveCarrinho), codigoCupom)
      .then(valor => { if (atual) setCotacao({ ...valor, chaveCarrinho, codigo: codigoCupom }); })
      .catch(error => { if (atual) setCupomErro(mensagemPedidoLoja(error)); })
      .finally(() => { if (atual) setValidandoCupom(false); });
    return () => { atual = false; };
  }, [vendedorId, chaveCarrinho, codigoCupom, tentativaCupom]);
  useEffect(() => { salvarCarrinho(vendedorId, carrinho); }, [vendedorId, carrinho]);
  useEffect(() => { setCarrinho(current => reconciliarCarrinho(current,produtos)); }, [produtos]);
  useEffect(() => {
    if (cliente?.uid && checkoutAguardandoLogin(vendedorId)) {
      aguardarLoginCheckout(vendedorId,false);
      if (carrinho.length) setCarrinhoAberto(true);
    }
  }, [cliente?.uid, vendedorId, carrinho.length]);

  // Disponíveis na loja: no site. Sem estoque não esconde mais o produto —
  // ele aparece automaticamente como "Sob encomenda".
  const disponiveis = produtos.filter(p => p.ativo !== false);

  // Categorias gerenciadas com produtos disponiveis, em ordem alfabetica,
  // disponível + qualquer categoria avulsa que apareça só nos produtos.
  const presentes = new Set(disponiveis.flatMap(categoriasDoProduto));
  const categoriasExibidas = [...new Set([
    ...categorias.filter(c => presentes.has(c)),
    ...[...presentes].filter(c => !categorias.includes(c)),
  ])].sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity:"base" }));

  const produtosFiltrados = disponiveis
    .filter(p => {
      if (filtro === "estoque") return p.estoque > 0;
      if (filtro === "encomenda") return !(p.estoque > 0);
      return true;
    })
    .filter(p => filtroCat === "todas" || categoriasDoProduto(p).includes(filtroCat))
    .filter(p => {
      if (!busca.trim()) return true;
      const normalize = value => (value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const t = normalize(busca.trim());
      return normalize(p.nome).includes(t) || normalize(p.descricao).includes(t);
    });

  const temEncomenda = disponiveis.some(p => !(p.estoque > 0));

  // ── Carrinho ──
  // Itens de pronta-entrega (com estoque) não passam do estoque; encomenda é livre.
  const adicionarCarrinho = (produto) => {
    const estoque = parseInt(produto.estoque) || 0;
    const encomenda = !(estoque > 0);
    let estourou = false;
    setCarrinho(c => {
      const existe = c.find(i => i.id === produto.id);
      if (existe) {
        if (!encomenda && existe.qtd >= estoque) { estourou = true; return c; }
        return c.map(i => i.id === produto.id ? { ...i, qtd: i.qtd + 1 } : i);
      }
      return [...c, {
        id: produto.id,
        nome: produto.nome,
        preco: parseFloat(produto.preco) || 0,
        fotoBase64: produto.fotoBase64 || produto.fotos?.find(f => f.tipo !== "video")?.url || null,
        encomenda,
        estoque,
        qtd: 1
      }];
    });
    if (estourou) toast(`Só há ${estoque} em estoque`, "warning");
    else toast("Adicionado ao carrinho 🛒", "success");
  };

  const mudarQtd = (id, delta) => {
    let estourou = false;
    setCarrinho(c => c
      .map(i => {
        if (i.id !== id) return i;
        if (delta > 0 && !i.encomenda && i.qtd >= (parseInt(i.estoque) || 0)) { estourou = true; return i; }
        return { ...i, qtd: i.qtd + delta };
      })
      .filter(i => i.qtd > 0)
    );
    if (estourou) toast("Quantidade máxima em estoque atingida", "warning");
  };

  const removerItem = (id) => setCarrinho(c => c.filter(i => i.id !== id));

  const totalItens = carrinho.reduce((s, c) => s + c.qtd, 0);
  const totalValor = carrinho.reduce((s, c) => s + c.preco * c.qtd, 0);
  const cotacaoAtual = cotacao?.chaveCarrinho === chaveCarrinho && cotacao.codigo === codigoCupom ? cotacao : null;
  const totalComDesconto = cotacaoAtual?.total ?? totalValor;
  const aplicarCupom = event => {
    event.preventDefault();
    const codigo = normalizarCupom(cupomInput);
    if (!/^[A-Z0-9_-]{1,40}$/.test(codigo)) { setCupomErro("Digite um código com letras, números, traço ou sublinhado."); return; }
    setCodigoCupom(codigo);
    setTentativaCupom(value => value + 1);
  };
  const removerCupom = () => { setCodigoCupom(""); setCotacao(null); setCupomInput(""); setCupomErro(""); };

  // ── Ir para o checkout ──
  const irParaCheckout = () => {
    if (authLoading || validandoCupom || (codigoCupom && !cotacaoAtual)) return;
    if (carrinho.length === 0) return;
    if (!cliente) {
      toast("Faça login para finalizar o pedido", "warning");
      setCarrinhoAberto(false);
      aguardarLoginCheckout(vendedorId,true);
      onPedirLogin?.();
      return;
    }
    setCarrinhoAberto(false);
    setCheckoutAberto(true);
  };

  // Pedido criado (após pagamento no checkout) -> abre o chat
  const aoPedidoCriado = (pedidoId) => {
    setCarrinho([]);
    removerCupom();
    setCheckoutAberto(false);
    setPedidoCriado({ id: pedidoId });
  };

  const filtros = [
    { id: "todos", label: "Todos" },
    { id: "estoque", label: "Pronta entrega" },
    ...(temEncomenda ? [{ id: "encomenda", label: "Sob encomenda" }] : [])
  ];

  return (
    <div className="store-catalog">
      <div className="store-catalog-heading"><h2>Explore a coleção</h2><p aria-live="polite">{produtosFiltrados.length} de {disponiveis.length} produtos</p></div>
      <div className="store-search">
        <CalcIcon name="search" size={20} />
        <input aria-label="Pesquisar produtos" value={busca} onChange={e => setBusca(e.target.value)} placeholder="O que você está procurando?" />
        {busca && <button type="button" className="store-search-clear" aria-label="Limpar busca" onClick={() => setBusca("")}>✕</button>}
      </div>
      <div className="store-filters" role="group" aria-label="Disponibilidade">
        {filtros.map(f => <button type="button" key={f.id} aria-pressed={filtro === f.id} onClick={() => setFiltro(f.id)}>{f.label}</button>)}
      </div>
      {categoriasExibidas.length > 0 && <div className="store-categories">
        <label htmlFor="store-category">Categoria</label>
        <select id="store-category" value={filtroCat} onChange={e => setFiltroCat(e.target.value)}>
          <option value="todas">🏷️ Todas as categorias</option>
          {categoriasExibidas.map(c => <option key={c} value={c}>{emojiDaCategoria(c)} {c}</option>)}
        </select>
      </div>}

      {/* Grid de produtos */}
      {produtosFiltrados.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px", opacity: 0.4 }}>🛍️</div>
          <p style={{ margin: 0 }}>{disponiveis.length ? "Nenhum produto combina com esses filtros." : "A coleção está sendo preparada. Volte em breve!"}</p>
          {(busca || filtro !== "todos" || filtroCat !== "todas") && <button type="button" className="store-add" style={{maxWidth:220,margin:"16px auto"}} onClick={() => {setBusca("");setFiltro("todos");setFiltroCat("todas");}}>Limpar filtros</button>}
        </div>
      ) : (
        <div className="store-grid">
          {produtosFiltrados.map(produto => <StoreProductCard key={produto.id} produto={produto}
            onOpen={() => setProdutoDetalhe(produto)} onAdd={() => adicionarCarrinho(produto)} />)}
        </div>
      )}

      {/* Botão flutuante do carrinho */}
      {totalItens > 0 && !carrinhoAberto && !pedidoCriado && !checkoutAberto && !produtoDetalhe && (
        <button
          className="store-cart-launcher"
          onClick={() => setCarrinhoAberto(true)}
          style={{
            position: "fixed", bottom: "24px", right: "24px",
            background: "#00d4aa", color: "#0a1628", padding: "16px 22px",
            border: "none", borderRadius: "30px", fontWeight: 700, fontSize: "14px",
            cursor: "pointer", boxShadow: "0 8px 24px rgba(0,212,170,0.4)",
            display: "flex", alignItems: "center", gap: "8px", zIndex: 50
          }}
        >
          🛒 Carrinho · {totalItens} · {fmtMoeda(totalComDesconto)}
        </button>
      )}

      {/* Drawer do carrinho */}
      {carrinhoAberto && (
        <div onClick={() => setCarrinhoAberto(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000
        }}>
          <div ref={cartRef} className="store-cart-drawer" role="dialog" aria-modal="true" aria-label="Seu carrinho" onClick={(e) => e.stopPropagation()} style={{
            position: "absolute", top: 0, right: 0, width: "100%", maxWidth: 420,
            height: "100vh", background: "#0a1628", borderLeft: "1px solid rgba(0,212,170,0.2)",
            display: "flex", flexDirection: "column"
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "18px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)"
            }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>🛒 Seu Carrinho</h2>
              <button aria-label="Fechar carrinho" onClick={() => setCarrinhoAberto(false)} style={{
                background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer"
              }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
              {carrinho.length === 0 ? (
                <div style={{ margin: "auto", color: "#64748b", textAlign: "center" }}>
                  Carrinho vazio
                </div>
              ) : carrinho.map(item => (
                <div key={item.id} className="store-cart-item" style={{
                  display: "flex", gap: 12, background: "#142b4a", borderRadius: 12, padding: 12
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 8, background: "#0a1628", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden"
                  }}>
                    {item.fotoBase64
                      ? <img src={item.fotoBase64} alt={item.nome} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: 24, opacity: 0.4 }}>📦</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{item.nome}</div>
                    {item.encomenda && <div style={{ fontSize: 10, color: "#f59e0b", marginBottom: 4 }}>Sob encomenda</div>}
                    <div style={{ fontSize: 13, color: "#00d4aa", fontWeight: 700 }}>{fmtMoeda(item.preco)}</div>
                  </div>
                  <div className="store-cart-controls" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between" }}>
                    <button aria-label={`Remover ${item.nome}`} onClick={() => removerItem(item.id)} style={{
                      background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14
                    }}>🗑️</button>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button aria-label={`Diminuir quantidade de ${item.nome}`} onClick={() => mudarQtd(item.id, -1)} style={qtdBtn}>−</button>
                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700, fontSize: 14 }}>{item.qtd}</span>
                      <button aria-label={`Aumentar quantidade de ${item.nome}`} onClick={() => mudarQtd(item.id, +1)} style={qtdBtn}>+</button>
                    </div>
                  </div>
                </div>
              ))}
              {carrinho.length > 0 && <section className="store-coupon" aria-label="Cupom de desconto">
                {codigoCupom ? <>
                  <div className="store-coupon-applied"><strong>{cotacaoAtual ? "Cupom aplicado: " : "Cupom: "}{codigoCupom}</strong><button type="button" onClick={removerCupom}>Remover cupom</button></div>
                  {cotacaoAtual?.cupom?.valorMinimo > 0 && <small>Compra mínima: {fmtMoeda(cotacaoAtual.cupom.valorMinimo)} em produtos, antes do desconto e sem entrega.</small>}
                  {validandoCupom && <p className="store-cart-feedback" role="status">Conferindo cupom e valores…</p>}
                  {!validandoCupom && cupomErro && <button className="store-checkout-retry" type="button" onClick={() => setTentativaCupom(value => value + 1)}>Tentar novamente</button>}
                </> : <form onSubmit={aplicarCupom}>
                  <label htmlFor="store-coupon-code">Tem um cupom de desconto?</label>
                  <div className="store-coupon-entry"><input id="store-coupon-code" value={cupomInput} onChange={e => { setCupomInput(e.target.value); setCupomErro(""); }} autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={40} placeholder="Digite o código" /><button type="submit" disabled={!cupomInput.trim()}>Aplicar</button></div>
                </form>}
                {cupomErro && <p className="store-cart-feedback" role="alert">{cupomErro}</p>}
                <small>Um cupom por pedido. A validade e os valores são conferidos novamente ao finalizar.</small>
              </section>}
            </div>

            {carrinho.length > 0 && (
              <div className="store-cart-footer" style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="store-cart-summary" aria-live="polite">
                  <div><span>Produtos</span><span>{fmtMoeda(cotacaoAtual?.subtotal ?? totalValor)}</span></div>
                  {cotacaoAtual?.desconto > 0 && <div className="store-cart-discount"><span>Desconto</span><span>− {fmtMoeda(cotacaoAtual.desconto)}</span></div>}
                  <div className="store-cart-total"><span>Total</span><strong>{fmtMoeda(totalComDesconto)}</strong></div>
                </div>
                {!cliente && (
                  <div style={{ fontSize: 12, color: "#f59e0b", marginBottom: 10, textAlign: "center" }}>
                    🔒 Você precisa entrar na sua conta para finalizar
                  </div>
                )}
                <button
                  onClick={irParaCheckout}
                  disabled={authLoading || validandoCupom || !!(codigoCupom && !cotacaoAtual)}
                  style={{
                    width: "100%", padding: 14, background: "#00d4aa", color: "#0a1628",
                    border: "none", borderRadius: 12, fontWeight: 800, fontSize: 15, cursor: "pointer"
                  }}
                >
                  {authLoading ? "Verificando sua conta…" : validandoCupom ? "Conferindo desconto…" : cliente ? "Finalizar pedido →" : "🔒 Entrar e finalizar"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de detalhe do produto — galeria completa + descrição + zoom */}
      {produtoDetalhe && (() => {
        const fotos = fotosDoProduto(produtoDetalhe);
        const temEstoqueDetalhe = produtoDetalhe.estoque > 0;
        return (
          <div onClick={() => setProdutoDetalhe(null)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1050,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16
          }}>
            <div ref={detailRef} className="store-product-dialog" role="dialog" aria-modal="true" aria-label={produtoDetalhe.nome} onClick={(e) => e.stopPropagation()} style={{
              width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto",
              background: "#0d1f38", borderRadius: 16, border: "1px solid rgba(0,212,170,0.25)",
              display: "flex", flexDirection: "column"
            }}>
              <div className="store-product-gallery" style={{ position: "relative", width: "100%", aspectRatio: "1", background: "#0a1628", flexShrink: 0 }}>
                {fotos.length > 0 ? (
                  fotos[fotoIdxDetalhe].tipo === "video" ? (
                    <VideoSlide key={fotos[fotoIdxDetalhe].url} url={fotos[fotoIdxDetalhe].url} pausado={lightboxIdx !== null} onClick={() => setLightboxIdx(fotoIdxDetalhe)} />
                  ) : (
                    <img src={fotos[fotoIdxDetalhe].url} alt={produtoDetalhe.nome} onClick={() => setLightboxIdx(fotoIdxDetalhe)}
                      style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "zoom-in" }} />
                  )
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 56, opacity: 0.3 }}>📦</div>
                )}
                <button aria-label="Fechar detalhes do produto" onClick={() => setProdutoDetalhe(null)} style={{
                  position: "absolute", top: 10, right: 10, width: 32, height: 32, borderRadius: "50%",
                  border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 16, fontWeight: 900, cursor: "pointer"
                }}>✕</button>
                {fotos.length > 1 && (
                  <>
                    <button onClick={() => setFotoIdxDetalhe(i => (i - 1 + fotos.length) % fotos.length)}
                      style={{ position: "absolute", top: "50%", left: 10, transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 18, fontWeight: 900, cursor: "pointer" }}>‹</button>
                    <button onClick={() => setFotoIdxDetalhe(i => (i + 1) % fotos.length)}
                      style={{ position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 18, fontWeight: 900, cursor: "pointer" }}>›</button>
                    <div style={{ position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 5 }}>
                      {fotos.map((_, i) => (
                        <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i === fotoIdxDetalhe ? "#fff" : "rgba(255,255,255,0.4)" }} />
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={{ padding: 20 }}>
                <h2 style={{ margin: "0 0 8px", fontSize: 19, fontWeight: 800 }}>{produtoDetalhe.nome}</h2>
                <Visualizador3D key={produtoDetalhe.id} modelo={visualizacao3dDoItem(produtoDetalhe)} nome={produtoDetalhe.nome} />
                {categoriasDoProduto(produtoDetalhe).length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                    {categoriasDoProduto(produtoDetalhe).map(cat => (
                      <span key={cat} style={{ display: "inline-block", background: "rgba(0,212,170,0.12)", color: "#00d4aa", fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20 }}>
                        {emojiDaCategoria(cat)} {cat}
                      </span>
                    ))}
                  </div>
                )}
                {produtoDetalhe.descricao && (
                  <p style={{ margin: "0 0 14px", fontSize: 13, color: "#94a3b8", lineHeight: 1.55 }}>{produtoDetalhe.descricao}</p>
                )}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ color: "#00d4aa", fontWeight: 800, fontSize: 22 }}>{fmtMoeda(produtoDetalhe.preco || 0)}</div>
                  <span style={{
                    background: temEstoqueDetalhe ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.15)",
                    color: temEstoqueDetalhe ? "#22c55e" : "#f59e0b",
                    padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700
                  }}>
                    {temEstoqueDetalhe ? "Pronta entrega" : "Sob encomenda"}
                  </span>
                </div>
                <button
                  onClick={() => { adicionarCarrinho(produtoDetalhe); setProdutoDetalhe(null); setCarrinhoAberto(true); }}
                  style={{
                    width: "100%", padding: 14, background: "#00d4aa", color: "#0a1628",
                    border: "none", borderRadius: 12, fontWeight: 800, fontSize: 15, cursor: "pointer"
                  }}
                >
                  {temEstoqueDetalhe ? "🛒 Adicionar ao carrinho" : "📋 Encomendar"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <Lightbox fotos={fotosDoProduto(produtoDetalhe)} indiceInicial={lightboxIdx || 0} aberto={lightboxIdx !== null} onFechar={() => setLightboxIdx(null)} />

      {/* Checkout (pagamento) */}
      {checkoutAberto && (
        <CheckoutLoja
          vendedor={vendedor}
          cliente={cliente}
          itens={carrinho}
          subtotal={totalValor}
          codigoCupom={codigoCupom}
          onCancelar={() => { setCheckoutAberto(false); setCarrinhoAberto(true); setTentativaCupom(value => value + 1); }}
          onPedidoCriado={aoPedidoCriado}
        />
      )}

      {/* Chat do pedido recém-criado */}
      {pedidoCriado && (
        <div onClick={() => setPedidoCriado(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1100,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: "100%", maxWidth: 460, background: "#0d1f38", borderRadius: 16,
            border: "1px solid rgba(0,212,170,0.25)", padding: 20,
            display: "flex", flexDirection: "column", maxHeight: "90vh"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h2 style={{ margin: 0, fontSize: 17 }}>🎉 Pedido enviado!</h2>
              <button onClick={() => setPedidoCriado(null)} style={{
                background: "none", border: "none", color: "#94a3b8", fontSize: 22, cursor: "pointer"
              }}>✕</button>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "#94a3b8" }}>
              Converse com o vendedor sobre seu pedido (cor, prazo, pagamento...).
            </p>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ChatPedido vendedorId={vendedorId} pedidoId={pedidoCriado.id} autor="cliente" altura={300} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const qtdBtn = {
  width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
  background: "#0a1628", color: "#f1f5f9", cursor: "pointer", fontSize: 16, fontWeight: 700,
  display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1
};
